import { initializeApp } from 'firebase/app'
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  reauthenticateWithCredential,
  updatePassword,
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth'
import { env } from './env'
import { isDemo } from './demo'
import { createSharedSession } from './shared-session'

const app = initializeApp(env.firebase)
export const auth = getAuth(app)

// Shared with the city: see shared-session.ts. Not in the dev demo, which has no Firebase user.
export const sharedSession = isDemo() ? null : createSharedSession(auth, env.apiBaseUrl)

export const signInWithGoogle = () => signInWithPopup(auth, new GoogleAuthProvider())

export const signInWithEmail = (email: string, password: string) => signInWithEmailAndPassword(auth, email, password)

export const logout = () => {
  // leaving the dev demo is its own sign-out
  if (import.meta.env.DEV && sessionStorage.getItem('metafor-demo')) {
    sessionStorage.removeItem('metafor-demo')
    window.location.replace('/login')
    return Promise.resolve()
  }
  return (sharedSession?.end() ?? Promise.resolve()).then(() => signOut(auth))
}

// The backend sends the verification email, not Firebase.
export async function sendVerificationEmail(user: User) {
  const token = await user.getIdToken()
  await fetch(`${env.apiBaseUrl}/api/v1/user/send-verification`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: '{}',
  })
}

// Firebase signs the new user in before the name and verification email are done;
// guest pages wait for the whole sign-up instead of bouncing mid-way.
let registering = false
const registeringListeners = new Set<() => void>()
export const registeringStore = {
  subscribe: (cb: () => void) => (registeringListeners.add(cb), () => registeringListeners.delete(cb)),
  get: () => registering,
}
const setRegistering = (v: boolean) => {
  registering = v
  registeringListeners.forEach((cb) => cb())
}

export async function registerWithEmail(name: string, email: string, password: string) {
  setRegistering(true)
  try {
    const { user } = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(user, { displayName: name })
    try {
      await sendVerificationEmail(user)
    } catch (err) {
      console.error('Error sending verification email from backend:', err)
    }
  } finally {
    setRegistering(false)
  }
}

// Profile edits. In the dev demo there is no Firebase user, so they only pretend.
const pretend = () => new Promise<void>((r) => setTimeout(r, 400))

// Firebase doesn't emit auth state for profile changes; useAuth listens here instead
let profileVersion = 0
const profileListeners = new Set<() => void>()
export const profileStore = {
  subscribe: (cb: () => void) => (profileListeners.add(cb), () => profileListeners.delete(cb)),
  version: () => profileVersion,
}
const profileChanged = () => {
  profileVersion++
  profileListeners.forEach((cb) => cb())
}

export async function updateDisplayName(user: User, displayName: string) {
  if (isDemo()) {
    await pretend()
    ;(user as { displayName: string | null }).displayName = displayName
    return profileChanged()
  }
  await updateProfile(user, { displayName })
  await user.reload()
  profileChanged()
}

/** Firebase wants a recent sign-in before a password change, so confirm the current one first. */
export async function changePassword(user: User, current: string, next: string) {
  if (isDemo()) return pretend()
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email ?? '', current))
  await updatePassword(user, next)
}
